'use client'

import { useEffect, useRef, useState, useCallback } from 'react'

const TURNSTILE_ENABLED = process.env.NEXT_PUBLIC_RM_TURNSTILE_ENABLED === '1'
const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_RM_TURNSTILE_SITE_KEY ?? ''

export function useTurnstile() {
    const [token, setToken] = useState<string | null>(null)
    const [container, setContainer] = useState<HTMLDivElement | null>(null)
    const widgetIdRef = useRef<string | null>(null)

    // Callback ref — triggers re-render when the DOM element mounts
    const containerRef = useCallback((node: HTMLDivElement | null) => {
        setContainer(node)
    }, [])

    const reset = useCallback(() => {
        if (widgetIdRef.current !== null && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current)
            setToken(null)
        }
    }, [])

    useEffect(() => {
        if (!TURNSTILE_ENABLED || !container) return

        let cancelled = false
        let pollTimer: ReturnType<typeof setTimeout>

        const initWidget = () => {
            if (cancelled || !container || !window.turnstile) return

            widgetIdRef.current = window.turnstile.render(container, {
                sitekey: TURNSTILE_SITE_KEY,
                callback: (t) => setToken(t),
                'expired-callback': () => setToken(null),
                'error-callback': () => setToken(null),
                theme: 'light',
            })
        }

        const poll = () => {
            if (cancelled) return
            if (window.turnstile) {
                initWidget()
            } else {
                pollTimer = setTimeout(poll, 100)
            }
        }

        poll()

        return () => {
            cancelled = true
            clearTimeout(pollTimer)
            if (widgetIdRef.current !== null && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current)
                widgetIdRef.current = null
            }
        }
    }, [container])

    if (!TURNSTILE_ENABLED) {
        return { containerRef, token: null, isReady: true, reset: () => {} }
    }

    return { containerRef, token, isReady: token !== null, reset }
}
